/**
 * What this module can propose — the means-test levers that do NOT involve moving house.
 *
 * THE BOUNDARY, and it is deliberate. Selling, downsizing, a retirement village and a granny flat
 * each already have a module with their own explainers, timing rules and traps. Reimplementing any
 * of them here would give the app five places that model a home sale, and they would drift. So this
 * engine owns the levers that leave the client exactly where they are, and the module links out for
 * the moves.
 *
 * Every amount here is SIZED FROM WHAT THE CLIENT ACTUALLY HAS. A candidate proposing a $300,000
 * annuity to someone with $80,000 in the bank is not a strategy, and an engine that generates one
 * has to be second-guessed by the reader — which defeats the point of it.
 */
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { annuityPreferenceOf, allowsResidual, allowsGuaranteed } from '../../../../shared/src/calculation/annuityPreference.ts';
import { formatCurrency } from '../../utils/index.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import { allocateNonConcessionalContribution, nonConcessionalCapacityForHousehold } from '../../../../shared/src/calculation/nonConcessionalContributions.ts';
import {
    FUNERAL_BOND_LIMIT,
    GIFTING_LIMIT_ANNUAL,
} from '../../constants/index.ts';

export interface CashflowPensionCandidate {
    id: string;
    name: string;
    description: string;
    changeNothing?: boolean;
    giftingAmount?: number;
    funeralBondPurchase?: number;
    annuity?: Record<string, unknown>;
    /** Which assets pay for it. Without this the shared levers do nothing at all. */
    fundingSources?: Array<{ type: string; id: string; amountToTake: number }>;
    /** An after-tax super contribution, per person (the engine caps each at what the rules allow). */
    superContribution?: { client?: number; partner?: number; commenceIncomeStream?: boolean };
    /** An investment (insurance) bond to this value. */
    investmentBond?: number;
    debtPayoff?: Record<string, unknown>;
}

const amountOf = (v: unknown): number => Math.max(0, parseFloat(String(v ?? '')) || 0);

/** A named pot the levers can actually draw from. */
interface LiquidSource {
    type: string;
    id: string;
    value: number;
}

/**
 * Cash and near-cash the client could actually move — not the house, not their super pension.
 *
 * Returned as SOURCES, not a total, because the shared levers need to be told which asset pays.
 * Given only an amount they liquidate nothing and apply nothing (see cashflowPension.ts's
 * applyCandidate), which produces a ranking of identical candidates rather than an error.
 *
 * Bank accounts first: spending cash costs nothing, whereas selling shares can crystallise CGT that
 * this module does not model and should not silently incur.
 */
function liquidSources(factFind: DraftFactFind): LiquidSource[] {
    const assets: any = factFind?.assets || {};
    const collect = (type: string) =>
        (assets[type] || [])
            .filter((row: any) => row?.id && amountOf(row?.value) > 0)
            .map((row: any) => ({ type, id: String(row.id), value: amountOf(row.value) }));
    return [...collect('bankAccounts'), ...collect('termDeposits'), ...collect('shares')];
}

function liquidAssets(factFind: DraftFactFind): number {
    return liquidSources(factFind).reduce((sum, source) => sum + source.value, 0);
}

/**
 * Draw `amount` across the available sources, largest-first within the bank-before-shares order.
 *
 * Keeps the emergency reserve intact by only ever being called with a committable amount.
 */
function fundingFor(sources: LiquidSource[], amount: number): Array<{ type: string; id: string; amountToTake: number }> {
    const plan: Array<{ type: string; id: string; amountToTake: number }> = [];
    let remaining = amount;
    for (const source of sources) {
        if (remaining <= 0) break;
        const take = Math.min(source.value, remaining);
        if (take <= 0) continue;
        plan.push({ type: source.type, id: source.id, amountToTake: take });
        remaining -= take;
    }
    return plan;
}

function isCouple(factFind: DraftFactFind): boolean {
    return factFind?.adviceFor === 'couple';
}

/**
 * A reserve the engine will not propose spending.
 *
 * Someone paying for help at home needs money they can reach this month. Every candidate here locks
 * capital away to some degree — a gift is gone, a bond is untouchable until death, an annuity is
 * committed for life — so proposing one that leaves nothing accessible would improve a means test
 * and damage the client.
 */
const EMERGENCY_RESERVE = 50_000;

/** What is available to commit, after keeping the reserve back. */
function committable(factFind: DraftFactFind): number {
    return Math.max(0, liquidAssets(factFind) - EMERGENCY_RESERVE);
}

export function generateCashflowPensionCandidates(
    factFind: DraftFactFind,
    /**
     * A provider's quoted annual income, where the client has one.
     *
     * Threaded into both annuity candidates rather than applied to the winner afterwards: a real
     * quote can change WHICH option wins, and a ranking struck on our indicative rate would then be
     * the wrong ranking (owner, 2026-09-01).
     */
    annuityIncomeOverride: number | null = null,
): CashflowPensionCandidate[] {
    const candidates: CashflowPensionCandidate[] = [
        {
            /*
             * THE BENCHMARK, and it is always first. Everything else is stated relative to it. An
             * engine with no do-nothing option ranks actions only against each other, so a strategy
             * that leaves the family worse off can still be crowned "best" — the exact defect the
             * Downsizer engine was caught with (2026-08-26).
             */
            id: 'cp-nothing',
            name: 'Change nothing',
            description: 'Your position as it stands, projected over the same period. Every option below is measured against this.',
            changeNothing: true,
        },
    ];

    const sources = liquidSources(factFind);
    const available = committable(factFind);
    const people = isCouple(factFind) ? 2 : 1;

    /*
     * FUNERAL BONDS — the cleanest of the levers and the smallest. Fully exempt up to the published
     * limit per person, and the money buys something most families pay for anyway. Sized at the
     * limit exactly: below it leaves exemption unused, above it is counted like any other asset, so
     * proposing more would be proposing a cost with no benefit.
     */
    const bondTotal = FUNERAL_BOND_LIMIT * people;
    if (available >= bondTotal) {
        candidates.push({
            id: 'cp-funeral-bond',
            name: `Funeral bond${people > 1 ? 's' : ''} — ${people > 1 ? 'one each' : 'up to the exempt limit'}`,
            description:
                `Move ${people > 1 ? 'up to the exempt limit each' : 'up to the exempt limit'} into a funeral bond. It stops counting for the assets test entirely, and it pays for something your family would otherwise fund from the estate.`,
            funeralBondPurchase: bondTotal,
            fundingSources: fundingFor(sources, bondTotal),
        });
    }

    /*
     * GIFTING — within the yearly free area only. Above it the excess is still counted, and still
     * deemed to earn income, for five years under the deprivation rules: the money has gone but the
     * means test has not noticed. The engine cannot weigh that five-year cost against a family's own
     * reasons for a larger gift, so it does not try.
     */
    if (available >= GIFTING_LIMIT_ANNUAL) {
        candidates.push({
            id: 'cp-gift',
            name: 'Gift within the yearly free area',
            description:
                'Give away up to the yearly free area. Inside that limit it leaves the assets test straight away — above it, the excess is still counted for five years even though the money is gone.',
            giftingAmount: GIFTING_LIMIT_ANNUAL,
            fundingSources: fundingFor(sources, GIFTING_LIMIT_ANNUAL),
        });
    }

    /* Both together, where there is enough to do both without breaking the reserve. */
    if (available >= bondTotal + GIFTING_LIMIT_ANNUAL) {
        candidates.push({
            id: 'cp-bond-and-gift',
            name: 'Funeral bond and a gift',
            description: 'Both of the above together — the largest reduction available without committing capital for life.',
            funeralBondPurchase: bondTotal,
            giftingAmount: GIFTING_LIMIT_ANNUAL,
            fundingSources: fundingFor(sources, bondTotal + GIFTING_LIMIT_ANNUAL),
        });
    }

    /*
     * LIFETIME ANNUITY — the biggest lever and the one with the sharpest trade-off. Only 60% of the
     * purchase price counts as an asset, so it moves far more than a bond can, but the capital is
     * committed for life and cannot be recovered. Offered only where a meaningful amount is
     * available after the reserve; below that the loss of access buys too little to justify itself.
     */
    const MIN_ANNUITY = 50_000;
    const annuityAmount = Math.min(available * 0.5, 400_000);
    if (annuityAmount >= MIN_ANNUITY) {
        const rounded = Math.round(annuityAmount / 10_000) * 10_000;
        /*
         * BOTH ENDS OF THE RESIDUAL-CAPITAL TRADE-OFF.
         *
         * Centrelink assesses a lifetime annuity at 60% of its purchase price WHATEVER its residual
         * capital value — RCV changes the payment rate, not the assets-test treatment. But net worth
         * values an annuity at its RESIDUAL (assessment/netWorth.ts, deliberately), so offering only
         * the nil-RCV version meant every annuity option here lost the estate comparison by its full
         * purchase price on day one. It was ruled out before the comparison started, in a module
         * whose whole purpose is comparing the estate cost of improving cashflow and pension.
         *
         * Every other module offers both. This one was built after that fix went in and inherited
         * the original single-variant shape.
         */
        const annuityMeansTest = 'Only 60% of what you pay counts in the assets test, so it moves more than any other option here';
        const cpAnnuityPreference = annuityPreferenceOf(factFind as never);
        if (allowsResidual(cpAnnuityPreference, 0)) candidates.push({
            id: 'cp-annuity',
            name: 'Lifetime annuity (nil residual)',
            description:
                `Buy a lifetime income stream. ${annuityMeansTest} — but the money is committed for life and cannot be taken back. This version pays the most income and leaves nothing of the purchase price to your estate.`,
            annuity: { purchaseAmount: rounded, annuityType: 'lifetime', ownership: 'client', residualCapital: 0, incomeOverride: annuityIncomeOverride },
            fundingSources: fundingFor(sources, rounded),
        });
        if (allowsResidual(cpAnnuityPreference, 1)) candidates.push({
            id: 'cp-annuity-rcv',
            name: 'Lifetime annuity (capital returned)',
            description:
                `The same annuity, but with the purchase price coming back to the estate on an early death — in full within the first half of life expectancy, less after that — rather than kept by the provider. It pays less income, and Centrelink still counts only 60% either way.`,
            annuity: { purchaseAmount: rounded, annuityType: 'lifetime', ownership: 'client', residualCapital: 1, incomeOverride: annuityIncomeOverride },
            fundingSources: fundingFor(sources, rounded),
        });
        if (allowsGuaranteed(cpAnnuityPreference, factFind as never)) candidates.push({
            id: 'cp-annuity-guaranteed',
            name: 'Lifetime annuity (full amount guaranteed)',
            description:
                `A lifetime annuity and a life policy bought together, so the full purchase price comes back to your estate whenever death comes. It pays the least income of the three, and only the annuity half earns the 60% assets-test treatment; the policy is counted at what it cost.`,
            annuity: { purchaseAmount: rounded, annuityType: 'guaranteed', ownership: 'client', residualCapital: 1, incomeOverride: annuityIncomeOverride },
            fundingSources: fundingFor(sources, rounded),
        });
    }

    /*
     * AFTER-TAX SUPER (F.6) AND AN INVESTMENT BOND (F.5) — 17 Sep 2026.
     *
     * Neither moves the means test on its own — accumulation super for someone over Age Pension age,
     * a pension, and a bond are all counted and deemed like the cash they replace. What they change
     * is TAX: 15% (or nil in a pension) or the bond's 30% inside, instead of the client's own rate on
     * the same earnings. The exception is worth the most: a couple with one member under Age Pension
     * age, whose accumulation super Centrelink does not count until they reach it.
     *
     * Logic-based: super only where the person may contribute (under 75, room under the balance
     * bands); the bond only where they may not — offering both to the same person would rank a
     * 30% tax rate against a 15% one and call it a choice.
     */
    if (available >= MIN_ANNUITY) {
        const ff = factFind as unknown as FactFind;
        const superRoom = nonConcessionalCapacityForHousehold(ff);
        if (superRoom.total > 0) {
            const ncc = allocateNonConcessionalContribution(ff, Math.round(available / 10_000) * 10_000);
            const total = ncc.client + ncc.partner;
            if (total >= MIN_ANNUITY) {
                candidates.push({
                    id: 'cp-super-after-tax',
                    name: 'Move savings into super (after-tax contribution)',
                    description: `Contribute ${formatCurrency(total)} of your savings to super after tax${ncc.preferred ? ` — ${ncc.preferred === 'client' ? (factFind?.client?.firstName || 'the client') : (factFind?.partner?.firstName || 'the partner')} first, whose accumulation super is not counted by Centrelink until Age Pension age` : ', where the earnings are taxed at 15% (nil once it pays a pension) instead of at your own rate'}. Capped at what each of you may contribute now; the money stays yours and can be drawn on.`,
                    superContribution: { client: ncc.client, partner: ncc.partner, commenceIncomeStream: ncc.preferred == null },
                    fundingSources: fundingFor(sources, total),
                });
            }
        } else {
            const bondAmount = Math.round(Math.min(available * 0.5, 400_000) / 10_000) * 10_000;
            if (bondAmount >= MIN_ANNUITY) {
                candidates.push({
                    id: 'cp-invest-bond',
                    name: 'Move savings into an investment bond',
                    description: `Put ${formatCurrency(bondAmount)} of your savings into an investment bond — the tax-paid home for money super can no longer take. It pays its own tax inside (30%) and nothing on your tax return; after ten years withdrawals are tax-free. Centrelink counts it in full and deems it, like the cash it replaces, so this is about tax, not the pension. Best left alone for ten years.`,
                    investmentBond: bondAmount,
                    fundingSources: fundingFor(sources, bondAmount),
                });
            }
        }
    }

    return candidates;
}
